/* eslint-disable quote-props */
import merge from 'lodash/merge'
import makePromise from '../make-promise'
import { addBreadcrumb, captureException } from '../../sentry-config'
import { getAccessToken } from '../nested-app-auth/authHelper'
import { setAuthTokenGraphApi, getGraphUrl } from '../graphApi'

export default class OutlookRestRequest {
  constructor(office) {
    this.office = office
  }

  accessToken = null

  refreshAccessToken = async isNaaEnabled => {
    await makePromise(
      this.office.context.mailbox.item.saveAsync,
      this.office.context.mailbox.item
    )()

    if (isNaaEnabled) {
      this.accessToken = await getAccessToken([
        'user.read',
        'mail.read',
        'mail.readwrite',
        'mail.send',
        'offline_access',
      ])
      setAuthTokenGraphApi(this.accessToken)
    } else {
      this.accessToken = await makePromise(
        this.office.context.mailbox.getCallbackTokenAsync,
        this.office.context.mailbox
      )({ isRest: true })
    }

    if (!this.accessToken) {
      captureException(new Error('Can not get rest token'))
      throw new Error('Can not get rest token')
    }
  }

  getRestUrl = () => {
    if (this.office.context.mailbox.restUrl !== undefined) {
      return this.office.context.mailbox.restUrl
    } else {
      // Just assume Office 365
      return 'https://outlook.office.com/api'
    }
  }

  getBaseUrl = isNestedAppAuthEnabled =>
    isNestedAppAuthEnabled ? `${getGraphUrl()}` : `${this.getRestUrl()}/v2.0`

  request = async (path, requestInfo, attemptsLeft = 2, isNestedAppAuthEnabled) => {
    if (!this.accessToken) {
      await this.refreshAccessToken(isNestedAppAuthEnabled)
    }
    const requestPayload = merge(
      {
        headers: {
          Accept: 'application/json, text/plain, */*',
          Authorization: `Bearer ${this.accessToken || ''}`,
          'Cache-Control': 'no-cache',
          'Content-Type': 'application/json',
          Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
          Pragma: 'no-cache',
          Prefer: 'outlook.allow-unsafe-html',
        },
      },
      requestInfo
    )

    addBreadcrumb({
      category: 'ms-graph-api',
      message: 'Outlook REST Request',
      data: {
        path: path,
        method: requestInfo.method,
        baseUrl: this.getBaseUrl(isNestedAppAuthEnabled),
      },
    })
    try {
      const response = await fetch(
        `${this.getBaseUrl(isNestedAppAuthEnabled)}/${path}`,
        requestPayload
      )
      if (!response.ok) {
        switch (response.status) {
          case 404:
            break
          case 401:
            this.accessToken = null
            if (attemptsLeft <= 1) {
              return response
            }
            return this.request(path, requestInfo, attemptsLeft - 1)
          default:
            captureException(new Error(`${path} failed with status ${response.status}`), {
              extra: {
                name: 'Outlook REST Api Request Failure',
                outlook: {
                  request: requestInfo,
                  response: await response.clone().text(),
                },
              },
            })
        }
      }

      return response
    } catch (e) {
      captureException(new Error(`${path} failed`), {
        extra: {
          name: 'Outlook REST Api Request Failure (fetch)',
          outlook: {
            request: requestInfo,
            error: {
              stack: e.stack,
              message: e.message,
            },
          },
        },
      })
      return false
    }
  }

  get = (path, requestInfo, isNestedAppAuthEnabled) =>
    this.request(path, { ...requestInfo, method: 'GET' }, 2, isNestedAppAuthEnabled)

  post = (path, requestInfo) => this.request(path, { ...requestInfo, method: 'POST' })

  put = (path, requestInfo) => this.request(path, { ...requestInfo, method: 'PUT' })

  delete = (path, requestInfo) => this.request(path, { ...requestInfo, method: 'DELETE' })
}
